/**
 * CardScanActions — universal footer action row for every customer-facing
 * page reachable from a card scan. Guarantees "MY TAP" + "NEO" (formerly PORTAL)
 * are always visible no matter which sub-screen the user lands on
 * (main, loading, error, VIP delivery, etc.).
 *
 * The NEO button honors the location-level `neo_enabled` flag:
 *   - When enabled: tapping opens /manager/:cardId (NEO login)
 *   - When disabled: tapping shows an inline bilingual disabled message
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const API_BASE = window.location.origin;

export const CardScanActions = ({ cardId, showPoweredBy = true, className = '' }) => {
    const { t } = useTranslation();
    const [neoEnabled, setNeoEnabled] = useState(true); // default optimistic — clicking still gated

    useEffect(() => {
        if (!cardId) return;
        let ignore = false;
        fetch(`${API_BASE}/api/neo/scan-route/${cardId}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (!ignore && d) setNeoEnabled(!!d.neo_enabled); })
            .catch(() => {});
        return () => { ignore = true; };
    }, [cardId]);

    if (!cardId) return null;

    const handleNeoClick = (e) => {
        if (neoEnabled) return; // allow default navigation
        e.preventDefault();
        toast(t('landing.portalDisabled', 'NEO is not enabled for this location. Please contact your administrator.'), {
            id: 'neo-disabled',
            duration: 3000,
        });
    };

    return (
        <div
            className={`flex flex-col items-center gap-2 ${className}`}
            data-testid="card-scan-actions"
        >
            {showPoweredBy && (
                <p className="bg-black/30 backdrop-blur-sm px-4 py-1.5 rounded-full text-white/80 text-xs">
                    {t('reviewShield.guestPoweredBy', 'Powered by')}{' '}
                    <span className="text-emerald-400 font-semibold">YulOnTap</span>
                </p>
            )}
            <div className="flex items-center justify-center gap-2 flex-wrap">
                <a
                    href={`/staff/${cardId}`}
                    data-testid="my-tap-btn"
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-5 py-2 rounded-full transition-colors min-h-[36px] flex items-center"
                >
                    {t('landing.myTap', 'MY TAP')}
                </a>
                <a
                    href={`/manager/${cardId}`}
                    data-testid="neo-btn"
                    onClick={handleNeoClick}
                    className={`${neoEnabled ? 'bg-fuchsia-600 hover:bg-fuchsia-700' : 'bg-slate-700 opacity-70 cursor-not-allowed'} text-white text-xs font-semibold px-5 py-2 rounded-full transition-colors min-h-[36px] flex items-center`}
                    aria-disabled={!neoEnabled}
                >
                    {t('landing.portal', 'NEO')}
                </a>
            </div>
        </div>
    );
};

export default CardScanActions;
